import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { AlertTriangle, CheckCircle, Clock, TrendingUp, TrendingDown, FileText, DollarSign, RefreshCw, Download } from 'lucide-react';
import { supabase } from '../../../lib/supabase';



// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatAmount = (amount, currency = 'XOF') => {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
};

const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4'];

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, subtitle, icon: IconComponent, color, trend, trendLabel }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <IconComponent className="w-5 h-5 text-white" />
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${trend >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-sm font-medium text-gray-700 mt-0.5">{title}</p>
    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    {trendLabel && <p className="text-xs text-gray-400 mt-1">{trendLabel}</p>}
  </div>
);

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      {payload?.map((entry, i) => (
        <p key={i} style={{ color: entry?.color }} className="text-xs">
          {entry?.name}: <span className="font-medium">{formatter ? formatter(entry?.value) : entry?.value}</span>
        </p>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FinanceDisputeAnalyticsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('6m');

  // Raw data
  const [escrowTransactions, setEscrowTransactions] = useState([]);
  const [escrowDisputes, setEscrowDisputes] = useState([]);

  // Computed metrics
  const [summaryStats, setSummaryStats] = useState({
    totalDisputes: 0,
    resolvedDisputes: 0,
    avgResolutionDays: 0,
    evidenceSubmissionRate: 0,
    totalEscrowFunds: 0,
    releasedFunds: 0,
    heldFunds: 0,
    refundedFunds: 0,
  });

  const [disputeVolumeData, setDisputeVolumeData] = useState([]);
  const [resolutionTimelineData, setResolutionTimelineData] = useState([]);
  const [evidenceRateData, setEvidenceRateData] = useState([]);
  const [escrowFlowData, setEscrowFlowData] = useState([]);
  const [disputeStatusDist, setDisputeStatusDist] = useState([]);
  const [escrowStatusDist, setEscrowStatusDist] = useState([]);

  // ── Fetch data ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);

      // Determine date range
      const now = new Date();
      const monthsBack = period === '3m' ? 3 : period === '6m' ? 6 : period === '12m' ? 12 : 6;
      const since = new Date(now);
      since?.setMonth(since?.getMonth() - monthsBack);
      const sinceISO = since?.toISOString();

      // Fetch escrow transactions
      const { data: txData } = await supabase
        ?.from('escrow_transactions')
        ?.select('id, status, amount, currency, created_at, updated_at, released_at')
        ?.gte('created_at', sinceISO)
        ?.order('created_at', { ascending: true });

      // Fetch disputes
      const { data: dispData } = await supabase
        ?.from('escrow_disputes')
        ?.select('id, transaction_id, status, reason, created_at, updated_at, resolved_at, evidence_urls')
        ?.gte('created_at', sinceISO)
        ?.order('created_at', { ascending: true });

      const transactions = txData || [];
      const disputes = dispData || [];

      setEscrowTransactions(transactions);
      setEscrowDisputes(disputes);

      // ── Build monthly buckets ─────────────────────────────────────────────
      const buckets = {};
      for (let i = monthsBack - 1; i >= 0; i--) {
        const d = new Date(now);
        d?.setMonth(d?.getMonth() - i);
        const key = `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}`;
        const label = d?.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
        buckets[key] = {
          key, label,
          disputes: 0, resolved: 0, pending: 0,
          resolutionDays: [], evidenceCount: 0, disputeCount: 0,
          escrowIn: 0, escrowReleased: 0, escrowHeld: 0, escrowRefunded: 0,
          txCount: 0,
        };
      }

      // Fill transaction data
      transactions?.forEach((tx) => {
        const d = new Date(tx?.created_at);
        const key = `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, '0')}`;
        if (!buckets?.[key]) return;
        const amt = tx?.amount || 0;
        buckets[key].txCount++;
        buckets[key].escrowIn += amt;
        if (['RELEASED', 'COMPLETED']?.includes(tx?.status)) buckets[key].escrowReleased += amt;
        if (['FUNDS_HELD', 'PENDING', 'PAYMENT_PENDING']?.includes(tx?.status)) buckets[key].escrowHeld += amt;
        if (tx?.status === 'REFUNDED') buckets[key].escrowRefunded += amt;
      });

      // Fill dispute data
      disputes?.forEach((d) => {
        const dt = new Date(d?.created_at);
        const key = `${dt?.getFullYear()}-${String(dt?.getMonth() + 1)?.padStart(2, '0')}`;
        if (!buckets?.[key]) return;
        buckets[key].disputes++;
        buckets[key].disputeCount++;

        const hasEvidence = Array.isArray(d?.evidence_urls) ? d?.evidence_urls?.length > 0 : !!d?.evidence_urls;
        if (hasEvidence) buckets[key].evidenceCount++;

        if (['resolved', 'RESOLVED', 'closed', 'CLOSED']?.includes(d?.status)) {
          buckets[key].resolved++;
          if (d?.resolved_at) {
            const days = Math.round((new Date(d?.resolved_at) - new Date(d?.created_at)) / (1000 * 60 * 60 * 24));
            if (days >= 0) buckets?.[key]?.resolutionDays?.push(days);
          }
        } else {
          buckets[key].pending++;
        }
      });

      // ── Compute chart arrays ──────────────────────────────────────────────
      const bucketArr = Object.values(buckets);

      setDisputeVolumeData(bucketArr?.map(b => ({
        month: b?.label,
        'Nouveaux litiges': b?.disputes,
        'Résolus': b?.resolved,
        'En cours': b?.pending,
      })));

      setResolutionTimelineData(bucketArr?.map(b => ({
        month: b?.label,
        'Délai moyen (jours)': b?.resolutionDays?.length
          ? Math.round(b?.resolutionDays?.reduce((a, c) => a + c, 0) / b?.resolutionDays?.length)
          : null,
        'Litiges résolus': b?.resolved,
      })));

      setEvidenceRateData(bucketArr?.map(b => ({
        month: b?.label,
        'Taux soumission (%)': b?.disputeCount > 0
          ? Math.round((b?.evidenceCount / b?.disputeCount) * 100)
          : 0,
        'Litiges avec preuves': b?.evidenceCount,
        'Total litiges': b?.disputeCount,
      })));

      setEscrowFlowData(bucketArr?.map(b => ({
        month: b?.label,
        'Fonds entrants': Math.round(b?.escrowIn / 1000),
        'Libérés': Math.round(b?.escrowReleased / 1000),
        'Bloqués': Math.round(b?.escrowHeld / 1000),
        'Remboursés': Math.round(b?.escrowRefunded / 1000),
      })));

      // ── Dispute status distribution ───────────────────────────────────────
      const statusCount = {};
      disputes?.forEach(d => {
        const s = d?.status || 'unknown';
        statusCount[s] = (statusCount?.[s] || 0) + 1;
      });
      setDisputeStatusDist(Object.entries(statusCount)?.map(([name, value]) => ({ name, value })));

      // ── Escrow status distribution ────────────────────────────────────────
      const txStatusCount = {};
      transactions?.forEach(tx => {
        const s = tx?.status || 'unknown';
        txStatusCount[s] = (txStatusCount?.[s] || 0) + 1;
      });
      setEscrowStatusDist(Object.entries(txStatusCount)?.map(([name, value]) => ({ name, value })));

      // ── Summary stats ─────────────────────────────────────────────────────
      const totalDisputes = disputes?.length;
      const resolvedDisputes = disputes?.filter(d => ['resolved', 'RESOLVED', 'closed', 'CLOSED']?.includes(d?.status))?.length;
      const resolutionDaysAll = disputes
        ?.filter(d => d?.resolved_at)
        ?.map(d => Math.round((new Date(d?.resolved_at) - new Date(d?.created_at)) / (1000 * 60 * 60 * 24)))
        ?.filter(n => n >= 0);
      const avgResolutionDays = resolutionDaysAll?.length
        ? Math.round(resolutionDaysAll?.reduce((a, c) => a + c, 0) / resolutionDaysAll?.length)
        : 0;
      const disputesWithEvidence = disputes?.filter(d => {
        return Array.isArray(d?.evidence_urls) ? d?.evidence_urls?.length > 0 : !!d?.evidence_urls;
      })?.length;
      const evidenceSubmissionRate = totalDisputes > 0 ? Math.round((disputesWithEvidence / totalDisputes) * 100) : 0;

      const totalEscrowFunds = transactions?.reduce((s, t) => s + (t?.amount || 0), 0);
      const releasedFunds = transactions
        ?.filter(t => ['RELEASED', 'COMPLETED']?.includes(t?.status))
        ?.reduce((s, t) => s + (t?.amount || 0), 0);
      const heldFunds = transactions
        ?.filter(t => ['FUNDS_HELD', 'PENDING', 'PAYMENT_PENDING']?.includes(t?.status))
        ?.reduce((s, t) => s + (t?.amount || 0), 0);
      const refundedFunds = transactions
        ?.filter(t => t?.status === 'REFUNDED')
        ?.reduce((s, t) => s + (t?.amount || 0), 0);

      setSummaryStats({
        totalDisputes,
        resolvedDisputes,
        avgResolutionDays,
        evidenceSubmissionRate,
        totalEscrowFunds,
        releasedFunds,
        heldFunds,
        refundedFunds,
      });
    } catch (err) {
      console.error('FinanceDisputeAnalyticsPanel fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Export CSV ──────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const rows = [
      ['Mois', 'Nouveaux litiges', 'Résolus', 'En cours', 'Délai moyen (j)', 'Taux preuves (%)', 'Fonds entrants (k)', 'Libérés (k)', 'Bloqués (k)'],
    ];
    disputeVolumeData?.forEach((row, i) => {
      rows?.push([
        row?.month,
        row?.['Nouveaux litiges'],
        row?.['Résolus'],
        row?.['En cours'],
        resolutionTimelineData?.[i]?.['Délai moyen (jours)'] ?? '',
        evidenceRateData?.[i]?.['Taux soumission (%)'] ?? '',
        escrowFlowData?.[i]?.['Fonds entrants'] ?? '',
        escrowFlowData?.[i]?.['Libérés'] ?? '',
        escrowFlowData?.[i]?.['Bloqués'] ?? '',
      ]);
    });
    const csv = '\uFEFF' + rows?.map(r => r?.join(';'))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-analytics-${period}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)]?.map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="w-10 h-10 bg-gray-200 rounded-lg mb-3" />
              <div className="h-7 bg-gray-200 rounded w-20 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-32" />
            </div>
          ))}
        </div>
        {[...Array(4)]?.map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
            <div className="h-5 bg-gray-200 rounded w-48 mb-4" />
            <div className="h-64 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header Controls ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Analytiques Finance & Litiges</h3>
          <p className="text-sm text-gray-500 mt-0.5">Volumes de litiges, délais de résolution, preuves et flux séquestre</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Period selector */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {[{ v: '3m', l: '3 mois' }, { v: '6m', l: '6 mois' }, { v: '12m', l: '12 mois' }]?.map(opt => (
              <button
                key={opt?.v}
                onClick={() => setPeriod(opt?.v)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  period === opt?.v ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {opt?.l}
              </button>
            ))}
          </div>
          <button
            onClick={fetchData}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          <button
            onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Exporter CSV
          </button>
        </div>
      </div>

      {/* ── Summary Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total litiges"
          value={summaryStats?.totalDisputes}
          subtitle={`${summaryStats?.resolvedDisputes} résolus`}
          icon={AlertTriangle}
          color="bg-red-500"
          trend={summaryStats?.totalDisputes > 0 ? Math.round((summaryStats?.resolvedDisputes / summaryStats?.totalDisputes) * 100) - 50 : 0}
          trendLabel="vs taux résolution 50%"
        />
        <StatCard
          title="Délai moyen résolution"
          value={summaryStats?.avgResolutionDays > 0 ? `${summaryStats?.avgResolutionDays}j` : '—'}
          subtitle="Jours ouvrés estimés"
          icon={Clock}
          color="bg-amber-500"
        />
        <StatCard
          title="Taux soumission preuves"
          value={`${summaryStats?.evidenceSubmissionRate}%`}
          subtitle={`Sur ${summaryStats?.totalDisputes} litiges`}
          icon={FileText}
          color="bg-blue-500"
        />
        <StatCard
          title="Fonds séquestre totaux"
          value={formatAmount(summaryStats?.totalEscrowFunds)}
          subtitle="Période sélectionnée"
          icon={DollarSign}
          color="bg-green-500"
        />
        <StatCard
          title="Fonds libérés"
          value={formatAmount(summaryStats?.releasedFunds)}
          subtitle="Transactions complétées"
          icon={CheckCircle}
          color="bg-emerald-500"
        />
        <StatCard
          title="Fonds bloqués"
          value={formatAmount(summaryStats?.heldFunds)}
          subtitle="En attente de libération"
          icon={Clock}
          color="bg-orange-500"
        />
        <StatCard
          title="Fonds remboursés"
          value={formatAmount(summaryStats?.refundedFunds)}
          subtitle="Remboursements acheteurs"
          icon={TrendingDown}
          color="bg-purple-500"
        />
        <StatCard
          title="Taux résolution"
          value={summaryStats?.totalDisputes > 0 ? `${Math.round((summaryStats?.resolvedDisputes / summaryStats?.totalDisputes) * 100)}%` : '—'}
          subtitle={`${summaryStats?.resolvedDisputes} / ${summaryStats?.totalDisputes}`}
          icon={TrendingUp}
          color="bg-cyan-500"
        />
      </div>

      {/* ── Row 1: Dispute Volumes + Status Distribution ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Dispute Volume Bar Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-gray-900">Volumes de litiges par mois</h4>
              <p className="text-xs text-gray-500 mt-0.5">Nouveaux, résolus et en cours</p>
            </div>
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          {disputeVolumeData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={disputeVolumeData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Nouveaux litiges" fill="#ef4444" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Résolus" fill="#10b981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="En cours" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Aucune donnée disponible</div>
          )}
        </div>

        {/* Dispute Status Pie */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-gray-900">Répartition des litiges</h4>
              <p className="text-xs text-gray-500 mt-0.5">Par statut actuel</p>
            </div>
          </div>
          {disputeStatusDist?.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={disputeStatusDist} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {disputeStatusDist?.map((_, i) => (
                      <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [v, 'Litiges']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1.5">
                {disputeStatusDist?.map((entry, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS?.[i % COLORS?.length] }} />
                      <span className="text-gray-600 capitalize">{entry?.name}</span>
                    </div>
                    <span className="font-semibold text-gray-800">{entry?.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Aucun litige</div>
          )}
        </div>
      </div>

      {/* ── Row 2: Resolution Timeline ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-gray-900">Délais de résolution des litiges</h4>
            <p className="text-xs text-gray-500 mt-0.5">Délai moyen en jours par mois</p>
          </div>
          <Clock className="w-5 h-5 text-amber-400" />
        </div>
        {resolutionTimelineData?.some(d => d?.['Délai moyen (jours)'] !== null) ? (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={resolutionTimelineData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} unit="j" />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="Délai moyen (jours)"
                stroke="#f59e0b"
                strokeWidth={2.5}
                dot={{ r: 4, fill: '#f59e0b' }}
                connectNulls
              />
              <Bar
                yAxisId="right"
                dataKey="Litiges résolus"
                fill="#10b981"
                fillOpacity={0.3}
                radius={[3, 3, 0, 0]}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-60 flex items-center justify-center text-gray-400 text-sm">Aucun litige résolu dans la période</div>
        )}
      </div>

      {/* ── Row 3: Evidence Submission Rate ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-gray-900">Taux de soumission de preuves</h4>
            <p className="text-xs text-gray-500 mt-0.5">Pourcentage de litiges avec preuves soumises</p>
          </div>
          <FileText className="w-5 h-5 text-blue-400" />
        </div>
        {evidenceRateData?.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={evidenceRateData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="evidenceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
              <Tooltip content={<CustomTooltip formatter={(v) => `${v}%`} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="Taux soumission (%)"
                stroke="#3b82f6"
                strokeWidth={2.5}
                fill="url(#evidenceGrad)"
                dot={{ r: 4, fill: '#3b82f6' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-60 flex items-center justify-center text-gray-400 text-sm">Aucune donnée disponible</div>
        )}
      </div>

      {/* ── Row 4: Escrow Fund Flow ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Escrow Flow Area Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-gray-900">Flux de fonds séquestre</h4>
              <p className="text-xs text-gray-500 mt-0.5">Entrants, libérés, bloqués et remboursés (en milliers XOF)</p>
            </div>
            <DollarSign className="w-5 h-5 text-green-400" />
          </div>
          {escrowFlowData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={escrowFlowData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="inGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="relGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="heldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit="k" />
                <Tooltip content={<CustomTooltip formatter={(v) => `${v}k XOF`} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="Fonds entrants" stroke="#3b82f6" strokeWidth={2} fill="url(#inGrad)" />
                <Area type="monotone" dataKey="Libérés" stroke="#10b981" strokeWidth={2} fill="url(#relGrad)" />
                <Area type="monotone" dataKey="Bloqués" stroke="#f59e0b" strokeWidth={2} fill="url(#heldGrad)" />
                <Area type="monotone" dataKey="Remboursés" stroke="#8b5cf6" strokeWidth={2} fill="none" strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Aucune transaction dans la période</div>
          )}
        </div>

        {/* Escrow Status Pie */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-gray-900">Statuts séquestre</h4>
              <p className="text-xs text-gray-500 mt-0.5">Répartition des transactions</p>
            </div>
          </div>
          {escrowStatusDist?.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={escrowStatusDist} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {escrowStatusDist?.map((_, i) => (
                      <Cell key={i} fill={COLORS?.[i % COLORS?.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [v, 'Transactions']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1.5 max-h-40 overflow-y-auto">
                {escrowStatusDist?.map((entry, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS?.[i % COLORS?.length] }} />
                      <span className="text-gray-600 capitalize">{entry?.name}</span>
                    </div>
                    <span className="font-semibold text-gray-800">{entry?.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Aucune transaction</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinanceDisputeAnalyticsPanel;
